/** Mirrors apps/server/src/modules/ops/motor/date.util.ts (buildCalendario, nextBusinessDay) */

import { IFeriado, IJornadaConfig } from '../interfaces/configuracao';
import { minutosPorDiaSemana } from './jornada';

/**
 * Teto da busca por um dia útil, como no motor: uma configuração sem nenhum dia
 * trabalhado faria o laço girar para sempre.
 */
const MAX_DIAS_BUSCA = 366;

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** O calendário de produção da fábrica: que dias rendem e que datas não contam. */
export interface ICalendarioFabrica {
  /** Índices de `Date.getUTCDay()` trabalhados: 0 é domingo, 6 é sábado. */
  diasTrabalhados: Set<number>;
  /** Datas 'YYYY-MM-DD' dos feriados que param a fábrica. */
  feriados: Set<string>;
}

/**
 * O calendário que o MOTOR usa, montado dos mesmos dois campos: a semana de trabalho
 * (um dia com zero minuto não rende) e os feriados ativos.
 *
 * `config.feriados` já vem só com os vivos (o arquivado fica em `feriadosExcluidos` e
 * não tira dia útil de ninguém), então aqui só resta o filtro de `ativo`: um feriado
 * desligado não para a fábrica.
 */
export function calendarioDaFabrica(
  jornada: Partial<IJornadaConfig> | undefined,
  feriados: ReadonlyArray<IFeriado> | undefined,
): ICalendarioFabrica {
  const minutos = minutosPorDiaSemana(jornada);
  const diasTrabalhados = new Set<number>();
  minutos.forEach((minutosDoDia, dia) => {
    if (minutosDoDia > 0) {
      diasTrabalhados.add(dia);
    }
  });

  return {
    diasTrabalhados,
    feriados: new Set((feriados ?? []).filter((feriado) => feriado.ativo).map((feriado) => feriado.data)),
  };
}

/** Uma data ISO lida em UTC, para o fuso de quem olha nunca empurrar o dia. */
function parseISO(dia: string): Date | null {
  const data = new Date(`${dia}T00:00:00.000Z`);
  return Number.isNaN(data.getTime()) ? null : data;
}

function toISO(data: Date): string {
  return data.toISOString().slice(0, 10);
}

/** O dia rende: a fábrica trabalha nele e nenhum feriado ativo o apaga. */
export function ehDiaUtil(dia: string, calendario: ICalendarioFabrica): boolean {
  const data = parseISO(dia);
  if (data === null) {
    return false;
  }
  return calendario.diasTrabalhados.has(data.getUTCDay()) && !calendario.feriados.has(dia);
}

/**
 * O primeiro dia útil DEPOIS de `dia`, pelo calendário da fábrica: pula fim de semana,
 * feriado ativo e qualquer dia da semana que a jornada não trabalha. É o
 * `nextBusinessDay` do motor, escrito aqui porque a tela precisa da data e o motor não
 * a publica.
 *
 * Devolve `null` quando a data é ilegível ou quando a configuração não trabalha nenhum
 * dia: inventar uma data nesse estado seria pior do que não responder.
 */
export function primeiroDiaUtilDepoisDe(dia: string, calendario: ICalendarioFabrica): string | null {
  const data = parseISO(dia);
  if (data === null) {
    return null;
  }

  for (let passo = 1; passo <= MAX_DIAS_BUSCA; passo += 1) {
    const candidato = toISO(new Date(data.getTime() + passo * MS_POR_DIA));
    if (ehDiaUtil(candidato, calendario)) {
      return candidato;
    }
  }
  return null;
}
